import { z } from 'zod';
import { InstanceId } from './common';
import { endpoint } from './endpoint';
import { lenient } from './lenient';

/** Local installation state. Only ACTIVE serves employees; unknown states read as MAINTENANCE. */
export const InstanceState = z.enum(['UNREGISTERED', 'INSTALLING', 'SETUP_REQUIRED', 'ACTIVE', 'MAINTENANCE', 'FAILED']);
export type InstanceState = z.infer<typeof InstanceState>;

/** Current wire protocol. Bumped only for breaking changes; features use capabilities. */
export const PROTOCOL_VERSION = 1;

export const EditWindow = z.enum(['1h', '24h', '7d', 'none']);
export type EditWindow = z.infer<typeof EditWindow>;

/** Delivery channel for invite/verification/reset mail. */
export const MailConnection = z.enum(['connected', 'not_configured', 'degraded']);

/** Attachment scanner health. */
export const ScannerHealth = z.enum(['ok', 'degraded', 'unavailable']);

/** Capabilities this version knows. Servers may announce more (ADR-0005); clients ignore the rest. */
export const Capability = z.enum(['messages.v1', 'files.v1', 'search.v1', 'invites.v1', 'mfa.totp', 'groups.history-sharing', 'reactions.v1', 'profile.photos', 'push.v1', 'typing.v1', 'messages.forward', 'messages.saved', 'messages.scheduled', 'files.thumbnails', 'push.hold']);
export type Capability = z.infer<typeof Capability>;

/** GET /.well-known/sd-messenger — identity the desktop app verifies before registering a server. */
export const WellKnown = z.object({
  product: z.literal('sd-messenger'),
  instanceId: InstanceId,
  companyName: z.string(),
  serverUrl: z.url(),
  apiBase: z.literal('/api/v1'),
  protocolVersion: z.number().int().positive(),
  state: lenient(InstanceState, 'MAINTENANCE'),
});
export type WellKnown = z.infer<typeof WellKnown>;

export const ServerInfo = z.object({
  instanceId: InstanceId,
  companyName: z.string(),
  serverUrl: z.url(),
  serverVersion: z.string(),
  protocolVersion: z.number().int().positive(),
  /**
   * Oldest app version this server accepts (older apps must update). There is
   * no upper bound: newer apps keep working with older servers of the same
   * `protocolVersion` and use capabilities for new features. `max` stays for
   * apps that read it and is always null.
   */
  supportedClients: z.object({ min: z.string(), max: z.string().nullable() }),
  state: lenient(InstanceState, 'MAINTENANCE'),
  /**
   * Changes when the database is restored from a backup: clients drop cached
   * state and reload. Optional like every field added to a response, so a
   * newer app still reads an older server.
   */
  syncGeneration: z.string().min(1).max(64).optional(),
  /** Open list so a newer server never fails an older client's check; test with `includes`. */
  capabilities: z.array(z.string().min(1).max(64)).max(100),
  limits: z.object({
    /** What the company allows per file, at most LIMITS.maxFileBytes. */
    maxFileBytes: z.number().int().positive(),
    maxFilesPerMessage: z.number().int().positive(),
    maxMessageChars: z.number().int().positive(),
    editWindow: lenient(EditWindow, 'none'),
    /** Programs and installers may be sent (the company owner allowed them). Absent from older servers: they may not. */
    executablesAllowed: z.boolean().optional(),
  }),
  mail: z.object({ status: lenient(MailConnection, 'degraded') }),
  /** Attachment scanner; `development-mock` must never be used in production. */
  scanner: z.object({
    mode: lenient(z.enum(['clamd', 'development-mock', 'disabled']), 'clamd'),
    status: lenient(ScannerHealth, 'degraded'),
  }),
});
export type ServerInfo = z.infer<typeof ServerInfo>;

export const serverInfoEndpoints = {
  wellKnown: endpoint({ id: 'meta.wellKnown', method: 'GET', path: '/.well-known/sd-messenger', auth: 'public', response: WellKnown }),
  serverInfo: endpoint({ id: 'meta.serverInfo', method: 'GET', path: '/api/v1/server-info', auth: 'public', response: ServerInfo }),
};
